import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { FinalResult, GameStateSnapshot } from '@juegazo/shared';
import { gameStore, loadSession } from '../lib/game-store';
import './background';

const fmt = (ms: number) => {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}:${String(r).padStart(2, '0')}` : `${r}s`;
};

@customElement('jgz-result')
export class JgzResult extends LitElement {
  createRenderRoot() {
    return this;
  }

  @property({ type: Object }) state: GameStateSnapshot | null = null;
  @property({ type: Object }) result: FinalResult | null = null;
  @state() private confirmReplay = false;
  @state() private requestingReplay = false;

  private get isAdmin(): boolean {
    const session = loadSession();
    const s = this.state;
    if (!s) return false;
    const me = s.players.find((p) => p.id === session.playerId);
    return me?.isAdmin ?? false;
  }

  private replay = () => {
    gameStore.send('room:start');
    this.confirmReplay = false;
  };

  private requestReplay = () => {
    if (!this.isAdmin) return;
    this.confirmReplay = true;
  };

  private cancelReplay = () => {
    this.confirmReplay = false;
  };

  private backHome = () => {
    gameStore.send('room:leave');
    this.dispatchEvent(new CustomEvent('jgz-leave', { bubbles: true, composed: true }));
  };

  render() {
    if (!this.result) {
      return html`
        <jgz-background></jgz-background>
        <div class="container fade-in" style="max-width: 480px; margin: 0 auto; text-align: center; padding-top: 4rem; position: relative; z-index: 1;">
          <div class="stack">
            <div style="font-size: 3.5rem;">⏳</div>
            <h1>Finalizando partida…</h1>
            <p class="muted">Si esto tarda demasiado, recarga la página.</p>
            <button class="btn-pill gray" @click=${this.backHome}>
              <span class="label">VOLVER AL INICIO</span>
            </button>
          </div>
        </div>
      `;
    }
    if (!this.result.finished && this.result.ranking.length === 0) {
      return html`
        <jgz-background></jgz-background>
        <div class="container fade-in" style="max-width: 480px; margin: 0 auto; position: relative; z-index: 1;">
          <div class="card" style="text-align: center;">
            <div style="font-size: 4rem;">🎲</div>
            <h1>Partida finalizada</h1>
            <p class="muted">No hay bombas completadas. No se declara ganador.</p>
            ${this.renderReplayControls()}
            <button class="btn-pill gray" @click=${this.backHome} style="margin-top: 0.6rem;">
              <span class="label">VOLVER AL INICIO</span>
            </button>
          </div>
        </div>
      `;
    }
    return html`
      <jgz-background></jgz-background>
      <div class="container fade-in" style="position: relative; z-index: 1;">
        <div class="stack">
          <div style="text-align: center;">
            <div style="font-size: 3.5rem;">🏆</div>
            <h1 style="font-size: 1.8rem; margin: 0;">Resultados</h1>
            ${this.result.winnerId
              ? html`<p class="chip" style="background: linear-gradient(135deg, var(--yellow), var(--orange)); color: #1F1F38; font-size: 1rem; padding: 0.7rem 1.3rem; margin: 1rem auto; font-weight: 800;">
                  🏆 ${this.findName(this.result.winnerId)} ganó
                </p>`
              : html`<p class="muted">No hay ganador declarado</p>`}
          </div>
          <div class="card" style="padding: 0; overflow: hidden;">
            <table style="width: 100%; border-collapse: collapse; font-size: 1rem;">
              <thead>
                <tr style="background: rgba(255,255,255,0.04);">
                  <th style="text-align: center; padding: 0.85rem; width: 50px;">#</th>
                  <th style="text-align: left; padding: 0.85rem;">Jugador</th>
                  <th style="text-align: right; padding: 0.85rem;">Tiempo</th>
                  <th style="text-align: right; padding: 0.85rem;">Penalización</th>
                  <th style="text-align: right; padding: 0.85rem;">💥</th>
                </tr>
              </thead>
              <tbody>
                ${this.result.ranking.map(
                  (entry, i) => html`
                    <tr
                      style=${entry.playerId === this.result!.winnerId
                        ? 'background: linear-gradient(90deg, rgba(253, 203, 110, 0.18), transparent);'
                        : i % 2 === 0
                          ? 'background: rgba(255,255,255,0.02);'
                          : ''}
                    >
                      <td style="text-align: center; padding: 0.85rem; font-weight: 800; color: var(--muted);">${i + 1}</td>
                      <td style="padding: 0.85rem;">
                        ${entry.playerId === this.result!.winnerId ? '🏆 ' : ''}${entry.displayName}
                      </td>
                      <td style="text-align: right; padding: 0.85rem; font-variant-numeric: tabular-nums;">${fmt(entry.totalMs)}</td>
                      <td style="text-align: right; padding: 0.85rem; font-variant-numeric: tabular-nums; color: var(--muted);">${fmt(entry.explosions * 5000)}</td>
                      <td style="text-align: right; padding: 0.85rem;">${entry.explosions}</td>
                    </tr>
                  `,
                )}
              </tbody>
            </table>
            <p class="muted" style="margin: 0.6rem 1rem 1rem; font-size: 0.82rem; text-align: right;">
              Penalización: informativo, no influye en el ranking.
            </p>
          </div>
          ${this.renderReplayControls()}
          <button class="btn-pill gray" @click=${this.backHome}>
            <span class="label">VOLVER AL INICIO</span>
          </button>
        </div>
      </div>
      ${this.confirmReplay ? this.renderConfirmReplay() : nothing}
    `;
  }

  private renderReplayControls() {
    if (!this.isAdmin) return nothing;
    return html`
      <button class="btn-pill primary" @click=${this.requestReplay}>
        <span class="label">JUGAR DE NUEVO</span>
        <span class="sublabel">Inicia la siguiente ronda</span>
      </button>
    `;
  }

  private renderConfirmReplay() {
    return html`
      <div
        style="position: fixed; inset: 0; background: rgba(5, 8, 17, 0.85); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 1.5rem;"
      >
        <div class="card fade-in" style="max-width: 480px; width: 100%; background: var(--bg-2);">
          <h2>¿Iniciar siguiente ronda?</h2>
          <p style="margin: 1rem 0;">La sala volverá a la pantalla de juego con la misma configuración.</p>
          <p class="muted" style="margin: 0.5rem 0;">Los jugadores permanecerán conectados.</p>
          <div class="row" style="justify-content: flex-end; margin-top: 1.5rem;">
            <button @click=${this.cancelReplay}>Cancelar</button>
            <button class="primary" @click=${this.replay}>Iniciar</button>
          </div>
        </div>
      </div>
    `;
  }

  private findName(id: string): string {
    return this.state?.players.find((p) => p.id === id)?.displayName ?? id;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'jgz-result': JgzResult;
  }
}
